/*  MUI  */
import { AutoFixNormal, Check, Refresh } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Grid,
    Paper,
    Skeleton,
    Typography
} from '@mui/material';

/*  Shared  */
import { PartnerBackButton } from 'application/Shared/Components/Buttons/BackButton';
import Token from 'application/Shared/Components/CodeBlock/Token';
import { Sizes } from 'application/Shared/Constants';
import { Notify, Response } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/* Hooks */
import { getUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

/*  Components  */
import Steps from './Components/Steps';

/*  Styles  */
const sxs = {
    grid: {
        apiKeyStep: { paddingRight: 20, paddingTop: 16 }
    },
    icon: {
        check: {
            height: Sizes[3],
            marginLeft: '2px',
            marginTop: '-5px',
            width: Sizes[3]
        },
        generate: {
            marginLeft: '-7px',
            marginRight: '5px',
            marginTop: '-3px'
        },
        regenerate: {
            marginLeft: '-7px',
            marginRight: '5px'
        }
    },
    link: {
        lci: { marginLeft: '4px' }
    }
};

/*  State  */
const state = () => ({
    key: '',
    loaded: false,
    secret: '',
    steps: []
});

class LCI extends Component {
    constructor(props) {
        super(props);
        this.state = state();

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        await this.loadAPIKey();
        this.loadSteps();
    };

    unload = () => {
        this.unmount = true;
        this.requests.cancel();
    };

    loadAPIKey = async () => {
        try {
            const response = await axios.get('/apiKey', {
                cancelToken: this.requests.token(),
                params: { scope: 'lci_integration' }
            });
            if (!Response.hasData(response)) {
                return;
            }

            const key = Response.getProperty(response, 'key');
            this.setState({ key });
        } catch (e) {
            debug.e('Error w/ loadConfig', e.toString());
        }
    };

    loadSteps = (params = {}) => {
        if (this.unmount) {
            return;
        }

        const { t } = this.props;
        const { key } = this.state;
        const { saving } = params;

        const url = 'https://master.lifecycleinsights.io/signin';

        const steps = [
            {
                component: (
                    <Box width="100%">
                        <span>
                            {t('Integrations.LCI.step1')}
                            {key && (
                                <Check
                                    style={sxs.icon.check}
                                    color="secondary"
                                />
                            )}
                        </span>
                        {this.renderAPIKeyStep({ saving })}
                    </Box>
                ),
                step: true
            },
            {
                component: (
                    <span>
                        Log into Lifecycle Insights. The login URL is:
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="link--secondary"
                            style={sxs.link.lci}
                            data-trackid="partner_integrations_lci_url"
                        >
                            {url}
                        </a>
                    </span>
                ),
                step: true
            },
            {
                component: (
                    <span>
                        Once logged in, notice the Left Navigation pane has an
                        Integrations Option under Administration.
                        <br />
                        Click on Integrations, and your main content pane will
                        show the list of available integration sources. Choose
                        Saaslio.
                    </span>
                ),
                step: true
            }
        ];

        this.setState({ loaded: true, steps });
    };

    generateAPIKey = async () => {
        this.loadSteps({ saving: true });

        try {
            const response = await axios.post('/apiKey', {
                overwrite: true,
                scope: 'lci_integration'
            });

            if (!Response.hasData(response)) {
                const err = 'Unable to Generate API Key';
                debug.e(err, response);
                Notify.e(err);
                this.loadSteps();
                return;
            }

            const data = Response.getData(response);
            const { key, secret } = data;

            Notify.s('API Key Generated');
            this.setState({ key, secret }, () => {
                this.loadSteps();
            });
        } catch (e) {
            debug.e('Error w/ generateAPIKey', e.toString());
            Notify.e('Unable to Generate API Key');
            this.loadSteps();
        }
    };

    renderKeyAndSecret = () => {
        const { t } = this.props;
        const { key, secret } = this.state;

        return (
            <Grid container spacing={3} style={sxs.grid.apiKeyStep}>
                <Grid item xs={12}>
                    <Alert severity="success" icon={false}>
                        {t('Integrations.api_key_created')}
                    </Alert>
                </Grid>
                <Grid item xs={12}>
                    <Token label={t('Common.api_key')} token={key} />
                </Grid>
                <Grid item xs={12}>
                    <Token label={t('Common.api_secret')} token={secret} />
                </Grid>
            </Grid>
        );
    };

    renderRegenerateKey = () => {
        const { t } = this.props;
        const { key } = this.state;

        return (
            <Grid container style={sxs.grid.apiKeyStep}>
                <Grid item xs={12} mb="16px">
                    <Button
                        data-trackid="partner_integrations_lci_regenerateKey"
                        variant="contained"
                        color="primary"
                        onClick={() => this.generateAPIKey()}
                    >
                        <Refresh style={sxs.icon.regenerate} fontSize="small" />
                        {t('APIKey.regenerate')}
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <Token label={t('Common.api_key')} token={key} />
                </Grid>
            </Grid>
        );
    };

    renderGenerate = () => {
        const { t } = this.props;

        return (
            <Grid container style={sxs.grid.apiKeyStep}>
                <Grid item xs={12}>
                    <Button
                        data-trackid="partner_integrations_lci_generateKey"
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => this.generateAPIKey()}
                    >
                        <AutoFixNormal style={sxs.icon.generate} />
                        {t('APIKey.generate')}
                    </Button>
                </Grid>
            </Grid>
        );
    };

    renderAPIKeyStep = (params) => {
        const { key, secret } = this.state;
        const { saving } = params;

        if (saving) {
            return (
                <Grid container spacing={3} style={sxs.grid.apiKeyStep}>
                    <Grid item xs={12}>
                        <Skeleton />
                        <Skeleton animation="wave" />
                        <Skeleton animation={false} />
                    </Grid>
                </Grid>
            );
        }

        if (secret && key) {
            return this.renderKeyAndSecret();
        }

        if (key) {
            return this.renderRegenerateKey();
        }

        return this.renderGenerate();
    };

    render() {
        const { session, t } = this.props;
        const { loaded, steps } = this.state;

        if (!loaded) {
            return null;
        }

        const userTheme = getUserTheme(session?.user?.theme);

        return (
            <>
                <Box width="100%">
                    <Typography variant="h3">
                        {t('Integrations.lci')}
                    </Typography>
                    <hr />
                </Box>
                <PartnerBackButton
                    linkTo="/integrations/partnerLevel"
                    textOverride="Integrations.Common.back"
                />
                <Paper
                    elevation={0}
                    variant="outlined"
                    style={{ padding: '20px' }}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <img
                                src={`/images/${userTheme}/integrations/lci.png`}
                                alt={t('Integrations.lci')}
                                style={{ width: '200px' }}
                            />
                            <hr />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h4">
                                {t('Integrations.instructions')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Steps steps={steps} />
                        </Grid>
                    </Grid>
                </Paper>
            </>
        );
    }
}
export default withTranslation()(LCI);
